import { db, type LiftXDatabase } from '../db';
import { replayExercise } from './personalRecordsRepository';
import { isLive, tombstoneById, tombstoneRows } from '../tombstone';

/**
 * Referential integrity. Phase 0 §5.4.
 *
 * IndexedDB has no foreign keys. The previous project relied on SQLite's 33 FK
 * constraints, so that responsibility moves here and has to be **explicit** —
 * this file is the whole of it, so there is one place to read and one place to
 * get wrong.
 *
 * The governing principle: **history is never collateral damage.** Deleting an
 * exercise or a plan must not delete the record of workouts that used them.
 * Only deleting the session itself deletes session data.
 *
 * Every cascade below **tombstones** (`deletedAt`, see `../tombstone.ts`)
 * rather than physically removing a row — "LiftX Sync Blueprint" §7. A row
 * that vanished from Dexie the instant it was deleted could never be pushed
 * to `liftx-sync`, so another device would never learn it was gone. The one
 * exception is `personalRecords`: not a synced table (it's a rebuildable
 * cache, Phase 0 §5.3), so it is still hard-deleted here exactly as before.
 *
 * | Rule                                   | Enforcement                  |
 * |----------------------------------------|------------------------------|
 * | Delete exercise → sessions/sets/plans survive | references nullified   |
 * | Delete exercise → its user media goes  | tombstoned, same transaction |
 * | Delete plan → sessions survive         | soft reference + snapshot    |
 * | Delete session → blocks/exercises/sets/segments go | tombstoned       |
 * | Orphans                                | reported, never auto-deleted |
 */

export interface IntegrityReport {
  /** sessionExercises pointing at an exercise id that no longer exists. */
  orphanedSessionExercises: number;
  /** planExercises pointing at a missing exercise. */
  orphanedPlanExercises: number;
  /** sessionSets whose parent sessionExercise is gone. */
  orphanedSessionSets: number;
  /** userMedia whose owning exercise is gone. */
  orphanedUserMedia: number;
  /** personalRecords for an exercise that no longer exists. */
  orphanedPersonalRecords: number;
  /** planDaySchedules whose planDay is gone. Master Phase 9. */
  orphanedPlanDaySchedules: number;
  /** progressionRules whose planExercise is gone. Master Phase 9. */
  orphanedProgressionRules: number;
  /** cardioSegments whose sessionExercise is gone. Master Phase 9. */
  orphanedCardioSegments: number;
  ok: boolean;
}

export interface IntegrityRepository {
  deleteExercise(id: string): Promise<void>;
  deleteSession(id: string): Promise<void>;
  deletePlan(id: string): Promise<void>;
  check(): Promise<IntegrityReport>;
}

export function createIntegrityRepository(database: LiftXDatabase = db): IntegrityRepository {
  return {
    /**
     * Delete an exercise **without** touching history.
     *
     * `exerciseId` is nulled on session and plan rows rather than the rows
     * being removed; `exerciseNameSnapshot` is what keeps history reading
     * "Barbell Bench Press, 80 kg × 10" instead of "(removed)".
     *
     * Personal records DO go: they are a rebuildable cache keyed by exercise,
     * not history (Phase 0 §5.3), and a PR for a nonexistent exercise is
     * meaningless rather than merely orphaned.
     */
    async deleteExercise(id: string): Promise<void> {
      const now = Date.now();
      await database.transaction(
        'rw',
        [
          database.exercises,
          database.userMedia,
          database.sessionExercises,
          database.planExercises,
          database.personalRecords,
        ],
        async () => {
          const sessionRefs = await database.sessionExercises.where('exerciseId').equals(id).toArray();
          for (const row of sessionRefs) {
            await database.sessionExercises.update(row.id, { exerciseId: null });
          }

          const planRefs = await database.planExercises.where('exerciseId').equals(id).toArray();
          for (const row of planRefs) {
            await database.planExercises.update(row.id, { exerciseId: null });
          }

          // Owned outright by the exercise — cascade.
          const media = await database.userMedia.where('exerciseId').equals(id).toArray();
          await tombstoneRows(database.userMedia, media, now);
          // Not synced — a rebuildable cache, still hard-deleted.
          await database.personalRecords.where('exerciseId').equals(id).delete();
          await tombstoneById(database.exercises, id, now);
        },
      );
    },

    /**
     * Delete a session and everything beneath it, in **one** transaction.
     *
     * Partial deletion is the failure mode worth designing against: a session
     * whose sets were removed but whose header survived would show as an empty
     * workout that cannot be repaired from the UI.
     *
     * Master Phase 7: also recomputes `personalRecords` for every exercise the
     * deleted session held a record for — `personalRecords` is a rebuildable
     * cache, and §12's data-safety rule ("History edited: PRs recomputed from
     * source sets") applies to a delete exactly as it does to an edit. Scoped
     * to just the affected exercises, not a full `rebuildAll()`, via
     * `replayExercise` — see its own doc comment.
     */
    async deleteSession(id: string): Promise<void> {
      const now = Date.now();
      await database.transaction(
        'rw',
        [
          database.sessions,
          database.sessionBlocks,
          database.sessionExercises,
          database.sessionSets,
          database.cardioSegments,
          database.personalRecords,
        ],
        async () => {
          const exercises = await database.sessionExercises.where('sessionId').equals(id).toArray();
          const exerciseIds = exercises.map((row) => row.id);

          // Determine which real exercises had a PR sourced from this
          // session, BEFORE the cascade tombstone removes the ability to
          // trace that — personalRecords.sessionId is the direct link
          // (schema v3).
          const affectedRecords = await database.personalRecords.where('sessionId').equals(id).toArray();
          const affectedExerciseIds = [...new Set(affectedRecords.map((row) => row.exerciseId))];

          for (const exerciseId of exerciseIds) {
            const sets = await database.sessionSets.where('sessionExerciseId').equals(exerciseId).toArray();
            await tombstoneRows(database.sessionSets, sets, now);
            const segments = await database.cardioSegments.where('sessionExerciseId').equals(exerciseId).toArray();
            await tombstoneRows(database.cardioSegments, segments, now);
          }

          await tombstoneRows(database.sessionExercises, exercises, now);
          const blocks = await database.sessionBlocks.where('sessionId').equals(id).toArray();
          await tombstoneRows(database.sessionBlocks, blocks, now);
          await tombstoneById(database.sessions, id, now);

          for (const exerciseId of affectedExerciseIds) {
            await replayExercise(database, exerciseId);
          }
        },
      );
    },

    /**
     * Delete a plan and its structure. Sessions built from it are untouched —
     * they hold `planId` as a soft reference plus an immutable `planSnapshot`,
     * so last month's workout stays readable after the plan is gone.
     */
    async deletePlan(id: string): Promise<void> {
      const now = Date.now();
      await database.transaction(
        'rw',
        [
          database.plans,
          database.planDays,
          database.planDaySchedules,
          database.planBlocks,
          database.planExercises,
          database.planSets,
          database.progressionRules,
        ],
        async () => {
          const days = await database.planDays.where('planId').equals(id).toArray();
          for (const day of days) {
            const blocks = await database.planBlocks.where('planDayId').equals(day.id).toArray();
            for (const block of blocks) {
              const planExercises = await database.planExercises
                .where('planBlockId')
                .equals(block.id)
                .toArray();
              for (const planExercise of planExercises) {
                const sets = await database.planSets.where('planExerciseId').equals(planExercise.id).toArray();
                await tombstoneRows(database.planSets, sets, now);
                const rules = await database.progressionRules
                  .where('planExerciseId')
                  .equals(planExercise.id)
                  .toArray();
                await tombstoneRows(database.progressionRules, rules, now);
              }
              await tombstoneRows(database.planExercises, planExercises, now);
            }
            await tombstoneRows(database.planBlocks, blocks, now);
            const schedules = await database.planDaySchedules.where('planDayId').equals(day.id).toArray();
            await tombstoneRows(database.planDaySchedules, schedules, now);
          }
          await tombstoneRows(database.planDays, days, now);
          await tombstoneById(database.plans, id, now);
        },
      );
    },

    /**
     * Report orphans. **Never deletes anything** (Phase 0 §5.4) — an automatic
     * cleanup that got the rule wrong would destroy data silently, so this
     * surfaces the problem and leaves the decision to a human.
     */
    async check(): Promise<IntegrityReport> {
      // A tombstoned row is, from every caller's perspective, already gone —
      // it must not count as "known" here, or a reference into it would be
      // wrongly reported as healthy.
      const exerciseIds = new Set((await database.exercises.filter(isLive).toArray()).map((row) => row.id));
      const sessionExerciseIds = new Set(
        (await database.sessionExercises.filter(isLive).toArray()).map((row) => row.id),
      );
      // Deliberately NOT checked: `sessions.planId` — a missing plan is a
      // soft reference by design (`deletePlan()`'s own doc comment), not
      // corruption. Flagging it would contradict this codebase's own data
      // model rather than surface a real problem.
      const planDayIds = new Set((await database.planDays.filter(isLive).toArray()).map((row) => row.id));
      const planExerciseIds = new Set((await database.planExercises.filter(isLive).toArray()).map((row) => row.id));

      const countMissing = async <T>(
        rows: T[],
        pick: (row: T) => string | null,
        known: Set<string>,
      ): Promise<number> => {
        let missing = 0;
        for (const row of rows) {
          const reference = pick(row);
          if (reference !== null && !known.has(reference)) missing += 1;
        }
        return missing;
      };

      // A tombstoned row is deleted data awaiting sync/purge, not a real
      // orphan needing attention — excluded from every candidate list below,
      // same as it is from the "known" sets above.
      const orphanedSessionExercises = await countMissing(
        await database.sessionExercises.filter(isLive).toArray(),
        (row) => row.exerciseId,
        exerciseIds,
      );
      const orphanedPlanExercises = await countMissing(
        await database.planExercises.filter(isLive).toArray(),
        (row) => row.exerciseId,
        exerciseIds,
      );
      const orphanedSessionSets = await countMissing(
        await database.sessionSets.filter(isLive).toArray(),
        (row) => row.sessionExerciseId,
        sessionExerciseIds,
      );
      const orphanedUserMedia = await countMissing(
        await database.userMedia.filter(isLive).toArray(),
        (row) => row.exerciseId,
        exerciseIds,
      );
      const orphanedPersonalRecords = await countMissing(
        await database.personalRecords.toArray(),
        (row) => row.exerciseId,
        exerciseIds,
      );
      const orphanedPlanDaySchedules = await countMissing(
        await database.planDaySchedules.filter(isLive).toArray(),
        (row) => row.planDayId,
        planDayIds,
      );
      const orphanedProgressionRules = await countMissing(
        await database.progressionRules.filter(isLive).toArray(),
        (row) => row.planExerciseId,
        planExerciseIds,
      );
      const orphanedCardioSegments = await countMissing(
        await database.cardioSegments.filter(isLive).toArray(),
        (row) => row.sessionExerciseId,
        sessionExerciseIds,
      );

      return {
        orphanedSessionExercises,
        orphanedPlanExercises,
        orphanedSessionSets,
        orphanedUserMedia,
        orphanedPersonalRecords,
        orphanedPlanDaySchedules,
        orphanedProgressionRules,
        orphanedCardioSegments,
        ok:
          orphanedSessionExercises === 0 &&
          orphanedPlanExercises === 0 &&
          orphanedSessionSets === 0 &&
          orphanedUserMedia === 0 &&
          orphanedPersonalRecords === 0 &&
          orphanedPlanDaySchedules === 0 &&
          orphanedProgressionRules === 0 &&
          orphanedCardioSegments === 0,
      };
    },
  };
}

export const integrityRepository = createIntegrityRepository();
